// FIXME: There may have been some regression/issue with ErrorFeedback elements
import {
  CountrySelect,
  CustomerSelect,
  CustomInput,
  ErrorFeedback,
  Fieldset,
  HintToggleButton,
  If,
  InputText,
  Select,
  StateSelect,
} from '@paytrace/pt-skel/Component'
import { BrandDetector } from '@paytrace/pt-skel/Helper/BrandDetector'
import { SelectOption } from '@paytrace/pt-skel/Component/SearchableDropdown'
import { expirationMonthOpts, expirationYearOpts } from '@paytrace/pt-skel/Component/Helper'
import { AppCtx, AppCtxI } from '@paytrace/pt-skel/Context'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import BaseEvent from '@paytrace/pt-skel/Event'
import { valueToState } from '@paytrace/pt-skel/Helper/Handler'

import debounce from 'lodash/debounce'
import { DebouncedFunc } from 'lodash'
import * as React from 'react'

import { DiscData } from '../Component'
import { ResponsePage } from '../Component/EnhancedData/ResponsePage/ResponsePage'
import { PatternGuardedInputText } from '../Component/PatternGuardedInputText'
import SectionTitle from '../Component/SectionTitle'
import { processCustomerDiscData, transactionSearchDDToCustomerDD } from '../Helper/DiscData'
import { ProcessButton } from '../Component/ProcessButton'
import Event from '../Event'
import { ActionType } from '../Helper/ActionType'
import { hasPermission, LegacyPermission } from '../Helper/HasPermission'
import { padZeroTwoDigits, parseDateFromMMDDYY } from '../Helper/util'

import * as T from '../types'

interface TransactionCardDetails {
  transaction_id: string
  credit_card_number: string
  exp_month: string
  exp_year: string
}

interface CardInfo {
  first_part: string
  last_part: string
  masked: string
  expiration_month?: string | ''
  expiration_year?: string | ''
  card_holder_name?: string | ''
}

interface Props {
  transactionId: string | null
  customerId: string | null
  action_type: ActionType | null
  card_info?: CardInfo | null
  swipe?: string | null
//   add other swipe transaction props here
}

interface State {
  [key: string]: any // Allow dynamic prop binding for DD fields - need 'any' type for other state vals

  auth_type: string
  customer_disc_data: T.CustomerDiscData[]
  customer_id: number
  amount: string
  credit_card_number: string
  exp_month: string
  exp_year: string
  csc: string

  billing_phone: string
  billing_email: string

  billing_name: string
  billing_address: string
  billing_address2: string
  billing_country: string
  billing_zip_code: string
  billing_city: string
  billing_state: string

  shipping_name: string
  shipping_address: string
  shipping_address2: string
  shipping_country: string
  shipping_zip_code: string
  shipping_city: string
  shipping_state: string
  is_billing_to_shipping_checked: boolean
  invoice_number: string
  tax_amount: string
  customer_reference_id: string
  description: string

  // Do we need to prune this off response/net request?
  disc_data_fields: T.DDPayload[]

  processorResponse?: T.ProcessorResponse
  goodResponse?: boolean

  isSubmitting: boolean
  isCofValid: boolean
  isLoadingCustomer: boolean

  canMerchantSurcharge: boolean
  incompleteSurchargeSetup: string[]
  isBillingStateSurchargeable: boolean
  isCardSurchargeable: boolean
  surchargeEffectiveRate: number
  surchargeAmount: number
  noSurchargeReasons: string[]
  omit_surcharge?: boolean
  force_omit_surcharge: boolean

  transactionId: string
  referencedTransactionSnapshot?: TransactionCardDetails
  canProcess: boolean
}

const EMPTY_CUSTOMER_ID = -1

const TRANSACTION_TYPES = [
  { name: 'Sale', val: LegacyPermission.SALE },
  { name: 'Authorization', val: LegacyPermission.ONLY },
  { name: 'Refund', val: LegacyPermission.UNREF_REFUND },
  // { name: 'Forced Sale', val: 'forced' },
  // { name: 'Store and Forward', val: 'store and forward' },
  // { name: 'Cash/Check', val: 'cash and check' },
]

const NONSURCHARGEABLE_AUTH_TYPES: (LegacyPermission | string)[] = [LegacyPermission.UNREF_REFUND]

function getAllowedTransactionTypes (records: T.PermissionCheckProps['records']) {
  return TRANSACTION_TYPES.filter(TRANSACTION_TYPES => hasPermission({ records, required_permissions: [TRANSACTION_TYPES.val], do_admin_bypass: false }))
}

function mapTransactionTypeToLegacyPermission (transactionType?: string| null): LegacyPermission | null {
  switch (transactionType) {
    case 'Sale':
      return LegacyPermission.SALE
    case 'Authorization':
      return LegacyPermission.ONLY
    case 'Refund':
      return LegacyPermission.UNREF_REFUND
    case 'Forced Sale':
      return null
    case 'Str/Fwd':
      return null
    default:
      return null
  }
}

function isNonSurchargeableAuthType (auth_type: LegacyPermission | string): boolean {
  return NONSURCHARGEABLE_AUTH_TYPES.includes(auth_type)
}

const MIN_BIN_LENGTH_FOR_PREVIEW = 6
const CHECK_SURCHARGE_DEBOUNCE_WAIT = 1000

function billingStateIsComplete (billingState: string | null): boolean {
  return '' !== billingState && billingState?.length === 2
}

function cleanCardNumberForPreview (cardNumber: string): string {
  return cardNumber.replace(/\D/g, '')
}

function resetFocus () {
  const focusInput = document.querySelector<HTMLInputElement>('input.default-focus')
  focusInput?.focus({ preventScroll: false })
}

class VirtualTerminal extends React.Component<Props, State> {
  context: AppCtxI
  static contextType = AppCtx
  private debouncedTransactionPreview: DebouncedFunc<() => void>

  constructor (props: Props) {
    super(props)
    this.state = {
      ...this.getInitialDataState(),
      customer_id: this.props.customerId ? parseInt(this.props.customerId) : EMPTY_CUSTOMER_ID,
      customer_disc_data: [],

      disc_data_fields: [],
      disc_data_field_values: [],

      processorResponse: undefined,
      goodResponse: undefined,

      isSubmitting: false,
      isCofValid: true,
      isLoadingCustomer: false,

      canMerchantSurcharge: false,
      incompleteSurchargeSetup: [],
      isBillingStateSurchargeable: false,
      isCardSurchargeable: false,
      surchargeEffectiveRate: 0.0,
      surchargeAmount: 0.0,
      noSurchargeReasons: [],
      omit_surcharge: undefined,
      force_omit_surcharge: false,
      is_billing_to_shipping_checked: false,
    }
    this.debouncedTransactionPreview = debounce(
      this.doTransactionPreviewRequest,
      CHECK_SURCHARGE_DEBOUNCE_WAIT
    )
  }

  private isSwipe () : boolean {
    return this.props.action_type === ActionType.SWIPE
  }

  private doTransactionPreviewRequest () {
    const { merchant: merchant_id } = this.context
    const { billing_state, amount, customer_id, credit_card_number, transactionId, processorResponse } = this.state

    const transactionPreviewSource = (() => {
      if (this.isSwipe()) {
        return { swipe: this.props.swipe }
      }

      const hasMaskedCard: boolean = credit_card_number.toLowerCase().includes('x')

      if (transactionId && hasMaskedCard) {
        return { transaction_id: transactionId }
      }

      const isCustomerSelected: boolean = Boolean(customer_id && customer_id !== EMPTY_CUSTOMER_ID)
      if (isCustomerSelected && hasMaskedCard) {
        return { customer_id }
      }

      if (credit_card_number) {
        return { card: { number: cleanCardNumberForPreview(credit_card_number) } }
      }

      return null
    })()

    if (!transactionPreviewSource) {
      return
    }

    const payload: {} = {
      merchant_id,
      amount,
      ...transactionPreviewSource,
    }

    if (billingStateIsComplete(billing_state)) {
      payload.billing_state = billing_state
    }

    this.setNoSurcharge('Checking surcharge eligibility...')
    PubSub.pub(Event.RUN_TRANSACTION_PREVIEW_REQUEST, payload)
  }

  private setMerchantSurchargeInfo () {
    const merchantRecord = this.context.records.merchant
    const newMessages: string[] = []

    if (!merchantRecord.can_merchant_surcharge && merchantRecord.is_surcharge_enabled) {
      if (!merchantRecord.is_merchant_agreed_to_surcharge_terms) {
        newMessages.push('Merchant has not yet agreed to surcharge terms.')
      }
      if (merchantRecord.is_past_surcharge_activation_date === false) {
        newMessages.push('Merchant activation date has not been reached.')
      }
    }

    this.setState(prevState => ({
      canMerchantSurcharge: merchantRecord.can_merchant_surcharge || false,
      incompleteSurchargeSetup: [...prevState.incompleteSurchargeSetup, ...newMessages],
    }))
  }

  public getInitialDataState () {
    const initialState = {
      auth_type: LegacyPermission.SALE,
      amount: '',
      credit_card_number: '',
      exp_month: '',
      exp_year: '',
      csc: '',

      billing_email: '',
      billing_phone: '',

      billing_name: '',
      billing_address: '',
      billing_address2: '',
      billing_country: 'US',
      billing_zip_code: '',
      billing_city: '',
      billing_state: '',

      shipping_name: '',
      shipping_address: '',
      shipping_address2: '',
      shipping_country: 'US',
      shipping_zip_code: '',
      shipping_city: '',
      shipping_state: '',

      invoice_number: '',
      description: '',
      tax_amount: '',
      customer_reference_id: '',

      transactionId: '',
      canProcess: true,
    }
    if (this.props.action_type === ActionType.SWIPE) {
      initialState.credit_card_number = this.props.card_info?.masked || ''
      initialState.exp_month = this.props.card_info?.expiration_month || ''
      initialState.exp_year = this.props.card_info?.expiration_year || ''
      initialState.billing_name = this.props.card_info?.card_holder_name || ''
    }
    return initialState
  }

  public setMerchantDefaultTransactionType () {
    const security_default_transaction_type = this.context?.records?.merchant?.security_default_transaction_type
    // Set auth_type from the merchant default security setting. If null or not matching set it to Sale
    this.setState({
      auth_type: mapTransactionTypeToLegacyPermission(security_default_transaction_type) || LegacyPermission.SALE,
    })
  }

  public setNoSurcharge (reason: string) {
    this.setState({
      isCardSurchargeable: false,
      surchargeEffectiveRate: 0.0,
      surchargeAmount: 0.0,
      noSurchargeReasons: [reason],
      force_omit_surcharge: true,
    })
  }

  public componentDidMount () {
    this.setMerchantDefaultTransactionType()
    this.setNoSurcharge('Enter a card number to determine surcharge eligibility.')

    this.setMerchantSurchargeInfo()
    const getAssociatedCustomerRequestEvent = () => {
      return (this.context.records.associatedMerchantList && this.context.records.misc.is_merchant_user)
        ? Event.GET_CUSTOMERS_FROM_TOKEN_REQUEST
        : Event.GET_CUSTOMER_LIST_REQUEST
    }
    if (this.props.customerId) {
      this.customerSelectCallback(this.props.customerId)
    }
    if (this.props.transactionId) {
      PubSub.pub(
        Event.GET_TRANSACTION_BY_ID_REQUEST,
        { transactionId: this.props.transactionId, merchantId: this.context.merchant }
      )
    } else {
      const associatedCustomerRequestEvent = getAssociatedCustomerRequestEvent()

      const customerRequestData = {
        include_associated_merchants: true,
        ...(associatedCustomerRequestEvent === Event.GET_CUSTOMER_LIST_REQUEST && { merchantId: this.context.merchant }),
      }

      PubSub.pub(associatedCustomerRequestEvent, customerRequestData)
    }

    PubSub.sub(this, Event.RUN_KEYED_SALE_RESPONSE, (json) => {
      this.setState({
        isSubmitting: false,
        processorResponse: json,
        // See: BFF_URL/redoc#tag/Response-Codes
        goodResponse: [101, 106, 109, 112].includes(json.response_code),
      })
      window.scrollTo(0, 0)
    })

    PubSub.sub(this, Event.RUN_SWIPE_SALE_RESPONSE, (json) => {
      this.setState({
        isSubmitting: false,
        processorResponse: json,
        goodResponse: [101, 106, 109, 112].includes(json.response_code),
      })
      window.scrollTo(0, 0)
    })

    // If we end up with a generic network error vs the actual backend processor
    // response, we need to take care of here (at least as far as disabling
    // the loader - the more generic error appears at top).
    PubSub.sub(this, Event.DONE_LOADING, ({ name }: { name: string }) => {
      if ('RUN_KEYED_SALE_REQUEST' === name) {
        this.setState({ isSubmitting: false })
      }
      if ('RUN_SWIPE_SALE_REQUEST' === name) {
        this.setState({ isSubmitting: false })
      }
    })

    PubSub.sub(this, Event.GET_CUSTOMER_REQUEST, ({ customerId }: { customerId: string }) => {
      if (Number(customerId) > 0) {
        this.setState({ isLoadingCustomer: true })
      }
    })

    PubSub.sub(
      this,
      Event.GET_CUSTOMER_RESPONSE,
      ({ customer, customerDiscData }: { customer: T.CustomerRecord, customerDiscData: any[] }) => {
        this.setState(prevState => {
          if (this.state.transactionId) {
            // auth by transaction will load details from previous transaction
            // don't want to overwrite with saved customer details
            return { isLoadingCustomer: false } as any
          }

          const discDataState = processCustomerDiscData(this.state.disc_data_fields, customerDiscData)

          // We use allow_partial to ensure *other* customer fields are populated, even
          // if the COF was unusable due to old enc or simply being blank.
          // Any good card will have some Xs in it for masking
          let isCofValid = (customer.card_masked || '').toLowerCase().includes('xxx')

          const expiryDate = parseDateFromMMDDYY((customer.exp_month || '01') + '-01-' + (customer.exp_year || '1970'))

          if (expiryDate && expiryDate < new Date()) {
            isCofValid = false
          }

          // The default to clear data is a negative customer id, so honor isCofValid there
          // As well as clear out other values that don't get cleared by the setState below
          // Such as disc data
          if ((customer.customer_id || EMPTY_CUSTOMER_ID) < 1) {
            isCofValid = true

            this.state.disc_data_fields.forEach((dd: { disc_id: number }) => {
              discDataState[`disc_id_${dd.disc_id}`] = ''
            })
          }

          const currentState = {
            isCofValid,
            customer_disc_data: customerDiscData,
            customer_id: customer.customer_id || EMPTY_CUSTOMER_ID,
            credit_card_number: customer.card_masked || '',

            billing_email: customer.email || '',
            billing_phone: customer.phone || '',

            billing_name: customer.name || '', // Is there a separate business name?
            billing_address: customer.address || '',
            billing_address2: customer.address2 || '',
            billing_country: customer.country || '',
            billing_zip_code: customer.zip_code || '',
            billing_city: customer.city || '',
            billing_state: customer.state || '',

            shipping_name: customer.shipping_name || '',
            shipping_address: customer.shipping_address || '',
            shipping_address2: customer.shipping_address2 || '',
            shipping_country: customer.shipping_country || '',
            shipping_zip_code: customer.shipping_zip_code || '',
            shipping_city: customer.shipping_city || '',
            shipping_state: customer.shipping_state || '',
            exp_year: customer.exp_year || '',
            exp_month: customer.exp_month || '',
            customer_reference_id: customer.customer_tax_id || '',
            description: customer.note || '',

            isLoadingCustomer: false,
            ...discDataState,

            isBillingStateSurchargeable: false,
            isCardSurchargeable: false,
            surchargeEffectiveRate: 0.0,
            surchargeAmount: 0.0,
            noSurchargeReasons: [],
            omit_surcharge: undefined,
            // if we're forcing omit_surchage due to an issue with merchant lookup, we should keep it true
            // if it's due to a customer-related issue, we can reset to false when changing customers
            force_omit_surcharge: (prevState.force_omit_surcharge && !prevState.canMerchantSurcharge),
          }
          if (this.isSwipe()) {
            currentState.isCofValid = false
            currentState.credit_card_number = prevState.credit_card_number
          }
          return currentState
        },
        () => {
          this.preCheck(0)
          resetFocus()
        })
      })

    PubSub.pub(Event.GET_DISC_DATA_REQUEST, { paytraceId: this.context.merchant })

    PubSub.sub(this, Event.GET_DISC_DATA_RESPONSE, (discData: T.DDPayload[]) => {
      // Bubble up the dynamic field assignments
      const discDataState: { [key: string]: string } = {}

      discData.forEach((dd_props: T.DDPayload) => {
        const key = `disc_id_${dd_props.disc_id}`
        discDataState[key] = ''
      })

      this.setState({ disc_data_fields: discData, ...discDataState })
    })


    PubSub.sub(this, Event.RUN_TRANSACTION_PREVIEW_RESPONSE, (response: T.TransactionPreviewResponse) => {
      if (response.status === 'error') {
        this.setNoSurcharge('Failed to determine card surcharge eligibility.')
      } else {
        let ineligibilityReason: string = response.data?.surcharge_preview_response?.surcharge_ineligibility_reason || ''
        const effectiveRate: number = response.data?.surcharge_preview_response?.surcharge_effective_rate || 0.0
        const surchargeAmount: number = response.data?.surcharge_preview_response?.surcharge_amount || 0.0
        const cardIsSurchargeable: boolean = response.data?.bin_preview_response?.is_surchargeable || false
        const canSurcharge: boolean = ineligibilityReason === '' && !!effectiveRate

        // We can show precisely what card was previewed by using the following property
        // const previewedMaskedCard: string = response.data?.card_info?.masked || ''

        if (canSurcharge) {
          this.setState({
            isCardSurchargeable: true,
            surchargeEffectiveRate: effectiveRate,
            surchargeAmount,
            isBillingStateSurchargeable: true,
            noSurchargeReasons: [],
            force_omit_surcharge: false,
          })
        } else {
          if (ineligibilityReason.toLowerCase() === 'Billing State is not surchargeable.'.toLowerCase()) {
            ineligibilityReason = 'Cardholder address is in a prohibited state.'
          } else {
            if (cardIsSurchargeable) {
              ineligibilityReason = 'This card is eligible for a surcharge. Provide the billing state and amount to view the exact surcharge fee.'
            } else {
              ineligibilityReason = 'The card is not eligible for a surcharge.'
            }
          }
          this.setNoSurcharge(ineligibilityReason)
          this.setState({
            isBillingStateSurchargeable: false,
            isCardSurchargeable: cardIsSurchargeable,
          })
        }
      }
    })

    PubSub.sub(self, Event.GET_TRANSACTION_BY_ID_RESPONSE, (response: T.TransactionSearchRecord) => {
      function mapTransactionResponseAuthType (processing_action: string | undefined, merchant_default_auth_type: string | null | undefined) {
        if (!processing_action) {
          return LegacyPermission.SALE
        }
        if (response.processing_action === 'auth_only') {
          return LegacyPermission.ONLY
        }
        if (response.processing_action === 'forced_sale') {
          // we use merchant settings for deriving the auth type, using sale as final fallback if it is not set either
          return mapTransactionTypeToLegacyPermission(merchant_default_auth_type) || LegacyPermission.SALE
        }

        return processing_action
      }

      if (response && response.transaction_id) {
        const discDataState = response.disc_data
          ? processCustomerDiscData(this.state.disc_data_fields, transactionSearchDDToCustomerDD(response.disc_data))
          : []

        const associatedCustomerRequestEvent = getAssociatedCustomerRequestEvent()

        const customerRequestData = {
          include_associated_merchants: true,
          includeCustomerId: response.customer_id,
          ...(associatedCustomerRequestEvent === Event.GET_CUSTOMER_LIST_REQUEST && { merchantId: this.context.merchant }),
        }

        PubSub.pub(associatedCustomerRequestEvent, customerRequestData)

        this.setState({
          transactionId: response.transaction_id,
          referencedTransactionSnapshot: {
            transaction_id: response.transaction_id,
            credit_card_number: response.card_masked || '',
            exp_month: padZeroTwoDigits(response.card_expiration_month) || '',
            exp_year: response.card_expiration_year || '',
          },
          auth_type: mapTransactionResponseAuthType(response.processing_action, this.context?.records?.merchant?.security_default_transaction_type),
          customer_id: response.customer_id || EMPTY_CUSTOMER_ID,
          amount: '',
          credit_card_number: response.card_masked || '',
          exp_month: padZeroTwoDigits(response.card_expiration_month) || '',
          exp_year: response.card_expiration_year || '',
          csc: '',

          billing_phone: response.billing_phone || '',
          billing_email: response.billing_email || '',
          billing_name: response.billing_name || '',
          billing_address: response.billing_address?.street || '',
          billing_address2: response.billing_address?.street2 || '',
          billing_country: response.billing_address?.country || '',
          billing_zip_code: response.billing_address?.postal_code || '',
          billing_city: response.billing_address?.city || '',
          billing_state: response.billing_address?.state || '',

          shipping_name: response.shipping_name || '',
          shipping_address: response.shipping_address?.street || '',
          shipping_address2: response.shipping_address?.street2 || '',
          shipping_country: response.shipping_address?.country || '',
          shipping_zip_code: response.shipping_address?.postal_code || '',
          shipping_city: response.shipping_address?.city || '',
          shipping_state: response.shipping_address?.state || '',

          invoice_number: response.invoice_number || '',
          tax_amount: '',
          customer_reference_id: response.customer_reference || '',
          description: response.description || '',
          ...discDataState,
        })
      }
    })
  }

  public componentWillUnmount () {
    PubSub.unsub(this)
    this.debouncedTransactionPreview.cancel()
  }

  public componentDidUpdate (_prevprops: Readonly<Props>, prevState: Readonly<State>): void {
    function hasBaseDataForTransactionPreview (currentState: Readonly<State>) {
      const hasCustomerId: boolean = currentState.customer_id !== EMPTY_CUSTOMER_ID
      const hasReferencedTransactionId: boolean = !!(currentState.transactionId)
      const hasCardBin: boolean = cleanCardNumberForPreview(currentState.credit_card_number).length >= MIN_BIN_LENGTH_FOR_PREVIEW
      return hasCustomerId || hasReferencedTransactionId || hasCardBin
    }

    function hasAmountChanged (currentState: Readonly<State>, previousState: Readonly<State>) {
      return previousState.amount !== currentState.amount
    }

    function hasBillingStateChanged (currentState: Readonly<State>, previousState: Readonly<State>) {
      const isDifferent: boolean = currentState.billing_state !== previousState.billing_state
      return isDifferent && billingStateIsComplete(currentState.billing_state)
    }

    function hasCCNumberChanged (currentState: Readonly<State>, previousState: Readonly<State>) {
      return currentState.credit_card_number !== previousState.credit_card_number
    }

    function hasSurchargeInputChanged (currentState: Readonly<State>, previousState: Readonly<State>) {
      return (
        hasBillingStateChanged(currentState, previousState) || hasCCNumberChanged(currentState, previousState)
      )
    }

    function hasAmountOrSurchargeInputChanged (currentState: Readonly<State>, previousState: Readonly<State>) {
      return hasAmountChanged(currentState, previousState) || hasSurchargeInputChanged(
        currentState, previousState
      )
    }

    // Checking for processor response prevents a preview call from the ResponseFeedback component because
    //  that component pubs to Event.GET_TRANSACTION_BY_ID_REQUEST and triggers the sub here. The existence of
    //  the processor response indicates that the ResponseFeedback component is active and no preview should run
    const hasProcessorResponse: boolean = Boolean(this.state.processorResponse)
    if (this.state.canMerchantSurcharge && !hasProcessorResponse) {
      if (hasAmountOrSurchargeInputChanged(this.state, prevState)) {
        this.setNoSurcharge('Surcharge criteria changed. Provide a card number to check surcharge eligibility.')
        if (hasBaseDataForTransactionPreview(this.state)) {
          this.debouncedTransactionPreview()
        }
      }
    }
  }

  public maybeRenderDiscData () {
    if (0 === this.state.disc_data_fields.length) {
      return <React.Fragment />
    }

    // onChange={valueToState(this.setState.bind(this), 'exp_year')}

    return (
      <React.Fragment>
        {this.state.disc_data_fields.map((dd_props: T.DDPayload, i: number) => {
          const key = `disc_id_${dd_props.disc_id}`
          const val = this.state[key] || ''

          return (
            <DiscData
              key={i}
              dd_props={dd_props}
              value={val}
              onChange={(e) => {
                // Special handling for multi-select in here...
                // It needs the chosen value set as an array
                if ('M' === dd_props.type) {
                  const anyE: any = e as any
                  const selectedOptions: any = anyE.target.selectedOptions
                  const chosen = Array
                    .from(selectedOptions)
                    .map(({ value }) => value)

                  this.setState({
                    [key]: chosen,
                  })
                } else {
                  // Everything else (normal select, input etc.)
                  const text = e.target.value
                  this.setState({
                    [key]: text,
                  })
                }
              }}
            />
          )
        })}
      </React.Fragment>
    )
  }

  public getDiscDataFromState (): { disc_id: number, value: string }[] {
    const discData: { disc_id: number, value: string }[] = []

    Object.keys(this.state).forEach(key => {
      const m = key.match(/^disc_id_([0-9]+)$/)

      if (m) {
        const value = this.state[key]

        // When using multi-select, each value is stored as a db row, so
        // expand to an entry in discData for each pair
        if (Array.isArray(value)) {
          value.forEach(val => {
            discData.push({ disc_id: Number(m[1]), value: val })
          })
        } else {
          discData.push({ disc_id: Number(m[1]), value })
        }
      }
    })

    return discData
  }

  public confirmSale = (): boolean => {
    const amt = (this.state.omit_surcharge || isNonSurchargeableAuthType(this.state.auth_type))
      ? this.state.amount
      : this.calculateTotalWithSurchargeAmount().toFixed(2)
    const confirmed = confirm(
      `Are you sure you want to process this transaction for $${amt}?`
    )

    return confirmed
  }

  // We used to be able to pass empty wrapping objects - now certain properties are being
  // validated/enforced due to the shared schema lib, so we have to prune off if empty.
  // This is easier/more logical than trying to track if user did or didn't put in data in those fields.
  // TODO: Confirm success with back end (other issues stopping that) before deleting
  // This is supposed to be fixed further back (BFF and beyond) to allow partial address objects
  public nullIfEmptyAddress (obj: { [key: string]: string | number }): { [key: string]: string | number } | null {
    let isEmpty: boolean = true

    Object.keys(obj).forEach(key => {
      // The country is always set due to select list defaults, but in isolation doesn't imply
      // an address as input by the user.
      if (!key.includes('country') && obj[key]) {
        isEmpty = false
      }
    })

    // The other day null was allowed - but it isn't by the front end now (need object)
    return isEmpty ? null : obj
  }

  public loadedTransactionCardUnchanged () {
    return (
      this.state.transactionId &&
      this.state.referencedTransactionSnapshot &&
      this.state.referencedTransactionSnapshot.credit_card_number === this.state.credit_card_number &&
      this.state.referencedTransactionSnapshot.exp_month === this.state.exp_month &&
      this.state.referencedTransactionSnapshot.exp_year === this.state.exp_year
    )
  }

  // This input structure maps the form values to the SDK expectation.
  // The SDK errors are relayed back to the form via the ErrorFeedback mappings.
  public stateToSale = (): any => {
    const disc_data_values = this.getDiscDataFromState()

    const data: any = {
      auth_type: this.state.auth_type,
      amount: Number(this.state.amount),
      swipe: '',
      card: {
        number: this.state.credit_card_number.replace(/[^0-9Xx]/g, ''),
        expiration_month: Number(this.state.exp_month),
        expiration_year: Number(this.state.exp_year),
      },
      csc: String(this.state.csc),
      merchant_id: this.context.merchant,
      customer_id: this.state.customer_id,
      // If the user edits pre-loaded card information from previous transaction
      // we'll invalidate 'auth by transaction' and try to fall back to a keyed auth or auth by customer
      transaction_id: this.loadedTransactionCardUnchanged() ? this.state.transactionId : '',
      billing_email: this.state.billing_email,

      // FIXME: The BFF/APIs do not use this - do we need it (legacy has it)?
      billing_phone: String(this.state.billing_phone).replace(/[^0-9]/g, ''),

      // FIXME: This doesn't exist on legacy frontend, but *is* part of
      // backend payload.
      shipping_email: '',

      billing_name: this.state.billing_name,
      billing_address: {
        city: this.state.billing_city,
        state: this.state.billing_state,
        country: this.state.billing_country,
        street: this.state.billing_address,
        street2: this.state.billing_address2,
        postal_code: String(this.state.billing_zip_code).replace(/[^ A-Za-z0-9]/g, ''),
      },
      shipping_name: this.state.shipping_name,
      shipping_address: {
        city: this.state.shipping_city,
        state: this.state.shipping_state,
        country: this.state.shipping_country,
        street: this.state.shipping_address,
        street2: this.state.shipping_address2,
        postal_code: String(this.state.shipping_zip_code).replace(/[^ A-Za-z0-9]/g, ''),
      },
      invoice_number: this.state.invoice_number,
      description: this.state.description,
      tax_amount: this.state.tax_amount,
      customer_reference_id: this.state.customer_reference_id,
      disc_data_values,
      surcharge_info: {
        omit_surcharge: this.state.force_omit_surcharge ? true : this.state.omit_surcharge,
      },
    }

    // The client side validator will complain if csc is present, even if null
    // So we need to remove it entirely if not using it based on security settings
    if (!this.state.csc) {
      delete data.csc
    }
    if (this.isSwipe()) {
      delete data.card
      data.swipe = this.props.swipe
    }

    return data
  }

  public preCheckTimeout: number = 0
  public preCheckThrottle: boolean = false

  public preCheck = (timeout = 3000) => {
    window.clearTimeout(this.preCheckTimeout)
    this.preCheckThrottle = true

    this.preCheckTimeout = window.setTimeout(() => {
      PubSub.pub('VALIDATION_ERROR::clear')

      // Only run checks sometime after the data auto-loads
      if (!this.state.isLoadingCustomer) {
        if (this.isSwipe()) {
          PubSub.pub(Event.RUN_SWIPE_SALE_CHECK, this.stateToSale())
        } else {
          PubSub.pub(Event.RUN_KEYED_SALE_CHECK, this.stateToSale())
        }
      }

      this.preCheckThrottle = false
    }, timeout)
  }

  public errorFeedbackTestFn = (key: string) => {
    return () => !this.preCheckThrottle &&
      0 < this.state[key].length
  }

  public clearForm = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLElement>
  ) => {
    e.preventDefault()
    e.stopPropagation()

    this.setState({ ...this.getInitialDataState() })
  }

  public handleBillingToShipping = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = e.target.checked

    if (isChecked) {
      this.setState(prevState => ({
        shipping_name: prevState.billing_name,
        shipping_city: prevState.billing_city,
        shipping_state: prevState.billing_state,
        shipping_country: prevState.billing_country,
        shipping_address: prevState.billing_address,
        shipping_address2: prevState.billing_address2,
        shipping_zip_code: prevState.billing_zip_code,
      }))
    } else {
      this.setState({
        shipping_name: '',
        shipping_city: '',
        shipping_state: '',
        shipping_country: '',
        shipping_address: '',
        shipping_address2: '',
        shipping_zip_code: '',
      })
    }

    this.setState({ is_billing_to_shipping_checked: isChecked })
  }

  public isCardBrandValid (): { valid: boolean, msg: string } {
    const { credit_card_number } = this.state

    if (!credit_card_number || credit_card_number.length < 4) {
      return { valid: true, msg: '' }
    }

    const brand = (new BrandDetector(credit_card_number)).getBrand()
    const allowed = this.context.records.company.accepted_card_types || []

    if (!allowed.includes(brand[1])) {
      return {
        valid: false,
        msg: `${brand[0]} is not an accepted card type for this account.`,
      }
    }

    return { valid: true, msg: '' }
  }

  public submit = (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLElement>
  ) => {
    e.preventDefault()
    e.stopPropagation()

    // Check for valid brand as well
    const cardBrandValid = this.isCardBrandValid()

    if (!cardBrandValid.valid) {
      alert(cardBrandValid.msg)

      return
    }

    if (!this.confirmSale()) {
      return
    }

    window.scrollTo(0, 0)
    this.setState({
      isSubmitting: true,
      processorResponse: undefined,
      goodResponse: undefined,
    })

    PubSub.pub('VALIDATION_ERROR::clear')
    PubSub.pub(BaseEvent.NETWORK_ERROR_CLEAR)
    if (this.isSwipe()) {
      PubSub.pub(Event.RUN_SWIPE_SALE_REQUEST, this.stateToSale())
    } else {
      PubSub.pub(Event.RUN_KEYED_SALE_REQUEST, this.stateToSale())
    }

    return false
  }

  private reprocess = () => {
    this.setNoSurcharge('Enter an amount to calculate a surcharge fee.')
    this.setState({
      processorResponse: undefined,
      goodResponse: undefined,
      amount: '',
    },
    resetFocus
    )
  }

  private renderTransaction () {
    // TODO: pull the emails from somewhere
    const emails: string[] = []
    // Email from transaction
    if (this.state.billing_email) {
      emails.push(this.state.billing_email)
    }

    // Email from merchant account
    if (this.context.records.company.email) {
      emails.push(this.context.records.company.email)
    }
    if (this.context.records.company.email2) {
      emails.push(this.context.records.company.email2)
    }

    if (this.context.records.user.email) {
      emails.push(this.context.records.user.email as string)
    }

    if (!this.state.processorResponse) {
      return <React.Fragment />
    }

    return (
      <ResponsePage
        {...this.state.processorResponse}
        amount={Number(this.state.amount)}
        emails={emails}
        merchant={Number(this.context.merchant)}
        customer_id={this.state.customer_id}
        onTransactionReprocess={this.reprocess}
        include_surcharge={this.state.canMerchantSurcharge}
      />
    )
  }

  public pageHeader () {
    if (this.isSwipe()) {
      return (
        <React.Fragment>
          <div className='float-right'>
            <HintToggleButton />
            <a className='help-link' href="https://paytrace.atlassian.net/wiki/spaces/HD/pages/2025914860/Swipe+Card">Help</a>
          </div>
          <h1>Swipe Card</h1>
          <hr className='clear' />
        </React.Fragment>
      )
    }
    return (
      <React.Fragment>
        <div className='float-right'>
          <HintToggleButton />
          <a className='help-link' href="https://paytrace.atlassian.net/wiki/spaces/HD/pages/2027585983/Key+Enter+Card">Help</a>
        </div>
        <h1>Key Enter Card</h1>
        <hr className='clear' />
      </React.Fragment>
    )
  }

  public getBrandFeedback () {
    const cardBrandValid = this.isCardBrandValid()

    if (cardBrandValid.valid) {
      return
    }

    return (
      <div className='error center error-feedback'>
        {cardBrandValid.msg}
      </div>
    )
  }

  public renderCofWarning () {
    if (this.state.isCofValid) {
      return <React.Fragment />
    }

    return (
      <div className='error'>
        The stored card on file is no longer valid (expired or corrupted).
        Please contact the customer to update the card details.  Other customer
        information has been populated, but please note the sale will go through
        as a standard Keyed Sale, and not a Card On File sale until the card
        details have been updated using the Customer Vault.
      </div>
    )
  }

  private isSelectedCof () : boolean {
    return !!this.state.customer_id && this.state.customer_id > 0
  }

  private calculateTotalWithSurchargeAmount (): number {
    return this.state.amount ? (parseFloat(this.state.amount) + this.state.surchargeAmount) : 0.0
  }

  private hasCantSurchargeReasons (): boolean {
    return this.state.noSurchargeReasons.length > 0
  }

  private hasIncompleteSurchargeReasons (): boolean {
    return this.state.incompleteSurchargeSetup.length > 0
  }

  private showNoSurchargeMessage (): boolean {
    return !(this.state.surchargeAmount > 0.0) && this.hasCantSurchargeReasons()
  }

  private showIncompleteSurchargeSetupMessage (): boolean {
    return !this.state.canMerchantSurcharge && this.hasIncompleteSurchargeReasons()
  }

  private maybeRenderSurchargeFeedback (): React.ReactElement {
    return (
      <>
        <div id='surcharge-feedback-block'>
          <If is={this.showNoSurchargeMessage() && !this.showIncompleteSurchargeSetupMessage()}>
            <div className='surcharge-info-message'>A surcharge cannot be applied to this transaction.</div>
            {this.state.noSurchargeReasons.slice(0, 1).map((reason, index) => <div className='surcharge-info-message' key={index}>{reason}</div>)}
          </If>
          <If is={this.showIncompleteSurchargeSetupMessage()}>
            <div className='surcharge-info-message'>A surcharge cannot be applied to this transaction. </div>
            {this.state.incompleteSurchargeSetup.slice(0, 1).map((item, index) => <div className='surcharge-info-message' key={index}>{item}</div>)}
          </If>
        </div>
      </>
    )
  }

  private getRequiredPermissionToProcess (): string {
    // As refund with this page is an un_referenced refund
    if (['refund', 'unreferenced_refund'].includes(this.state.auth_type)) {
      return LegacyPermission.CAN_PROCESS_UNREFERENCED_REFUNDS
    }
    if (!Object.values(LegacyPermission).includes(this.state.auth_type as any)) {
      return mapTransactionTypeToLegacyPermission(this.context?.records?.merchant?.security_default_transaction_type) || LegacyPermission.SALE
    }

    return this.state.auth_type
  }

  public customerSelectCallback = (customerId: string) => {
    const selectedCustomerId = parseInt(customerId)

    this.setState(prevState => {
      const isNewCustomer = prevState.customer_id !== selectedCustomerId

      return {
        ...prevState,
        ...(isNewCustomer ? this.getInitialDataState() : {}),
        // if we pre-loaded from a previous transaction and the selected customer is changed
        // then we need to clear the previous transaction reference
        transactionId: (prevState.transactionId && !isNewCustomer) ? prevState.transactionId : '',
        customer_id: selectedCustomerId,
        amount: prevState.amount,
      }
    }, () => { this.setMerchantDefaultTransactionType() })
    PubSub.pub(Event.GET_CUSTOMER_REQUEST, { customerId })
  }

  public render () {
    let statusMessage = <></>

    // For now, just make the form go away if a good response
    // If we have a bad one, continue to show it so user can
    // adjust the values easier.
    if (this.state.processorResponse) {
      statusMessage = this.renderTransaction()
    }

    const viewElement: { [key: string]: boolean } = this.context.records.merchant.viewbits_dict || {}
    const reqElement: { [key: string]: boolean } = {
      business_address: this.context.records.merchant.require_business_address || false,
      csc: this.context.records.merchant.require_csc || false,
      invoice_number: this.context.records.merchant.require_invoice_number || false,
      zip_code: this.context.records.merchant.require_zip_code || false,
    }

    let displayDropdown = this.context.records.merchant.display_drop_down
    const displayShippingElements = (viewElement.shipping_name || viewElement.shipping_address ||
      viewElement.shipping_address2 || viewElement.shipping_city ||
      viewElement.shipping_state || viewElement.shipping_zip || viewElement.shipping_country)

    // Respect the false, but default to true if missing
    if (displayDropdown === undefined) {
      displayDropdown = true
    }

    let formDisplay = this.state.isSubmitting ? 'none' : 'block'

    // Also enact the form hide for pending customer loaded data, as we don't
    // want the user to change fields, only for them to be overwritten during the callback
    if (!this.state.isSubmitting) {
      formDisplay = this.state.isLoadingCustomer ? 'none' : 'block'
    }

    if (this.state.processorResponse) {
      formDisplay = 'none'
    }

    // If we don't render an element due to conditional renders (isSubmitting) then
    // we have to re-render it when done - this means we lose any error listeners
    // and things like CustomerSelect will re-query the list (which we don't want).
    // If we want to show a loading spinner, hide with css, but not with an entire re-render.
    return (
      <>
        {this.pageHeader()}
        {statusMessage}

        <form
          style={{ display: formDisplay }}
          className='uid-virtual-terminal-form'
          autoComplete='on'
          onChange={() => this.preCheck()}
          onSubmit={this.submit}>

          <Fieldset className='field-section-vt'>
            <CustomerSelect
              className='uid-customer-select-vt'
              merchantId={this.context.merchant}
              selectedCustomerId={this.state.customer_id.toString()}
              handleSelectionChange={(selectOption: SelectOption) => this.customerSelectCallback(selectOption.val)}
              isMerchantUser={this.context.records.misc.is_merchant_user}
              associatedMerchantList={this.context.records.associatedMerchantList}
              fieldsetClassName='field-section-vt'
              hideFieldset={true}
            />
            <SectionTitle title='Transaction Details'/>
            <Select
              className='uid-transaction-type'
              label='Transaction Type'
              options={getAllowedTransactionTypes(this.context.records)}
              onChange={valueToState(this.setState.bind(this), 'auth_type')}
              value={this.state.auth_type}
              hintText='Please select what type of transaction you would like to process from the drop down menu. Options are sale, authorization, forced sale (a sale that was authorized offline), and refund.'
            />
            <If is={!!viewElement.invoice_number}>
              <InputText
                maxLength={50}
                className='uid-invoice-number default-focus'
                label='Invoice Number'
                onChange={valueToState(this.setState.bind(this), 'invoice_number')}
                value={this.state.invoice_number}
                required={reqElement.invoice_number}
                placeholder={reqElement.invoice_number ? '' : 'Optional'}
                hintText='Please provide an invoice number for this transaction that may contain up to 50 characters of free text.  The invoice number field may be used to provide an identifier for this transaction that relates to your accounting or inventory system, if applicable.'
                autoFocus={true}
              />
            </If>
            <PatternGuardedInputText
              className='uid-amount default-focus'
              onChange={valueToState(this.setState.bind(this), 'amount')}
              name='amount'
              label='Amount'
              pattern='^([0-9]{0,17}|([0-9]{0,15}\.[0-9]{0,2}))$'
              value={this.state.amount}
              placeholder='0.00'
              required
              hintText='Enter this amount as it would be entered on a calculator, with decimals, if necessary.'
              autoFocus={!viewElement.invoice_number}
            />
            <ErrorFeedback
              className='uid-amount-error-feedback'
              testFn={this.errorFeedbackTestFn('amount')}
              name='amount' />

            <If is={!!viewElement.tax}>
              <PatternGuardedInputText
                className='uid-tax'
                label='Tax Amount'
                pattern='^([0-9]{0,12}|([0-9]{0,10}\.[0-9]{0,2}))$'
                onChange={valueToState(this.setState.bind(this), 'tax_amount')}
                placeholder='Optional'
                value={this.state.tax_amount}
                hintText="Please provide the local tax amount that is included in the transaction amount.  The local tax amount may contain up to 12 numeric digits and a decimal.  The default value is 0."
              />
            </If>

          <SectionTitle title='Card Details'/>

            {this.renderCofWarning()}

            {/* Allow space/dash for user formatting convenience (maybe auto-mask) */}
            <PatternGuardedInputText
              className='uid-card-number'
              pattern='^[0-9xX \-]{0,22}$'
              name='card_number'
              label='Credit Card Number'
              autoComplete='off'
              onChange={valueToState(this.setState.bind(this), 'credit_card_number')}
              value={this.state.credit_card_number}
              placeholder='XXXX-XXXX-XXXX-XXXX'
              required
              hintText='Please provide the account/card number on which you wish to process the transaction. Card numbers may be entered with or without dashes and spaces.'
              disabled={this.isSwipe()}
            />
            {this.getBrandFeedback()}
            <ErrorFeedback
              className='uid-card-number-error-feedback'
              testFn={this.errorFeedbackTestFn('credit_card_number')}
              name='card/number' />

            <If is={!!displayDropdown}>
                <Select
                  className='uid-card-exp-month small-input'
                  label='Expiration Date'
                  options={expirationMonthOpts()}
                  onChange={valueToState(this.setState.bind(this), 'exp_month')}
                  value={this.state.exp_month}
                  required
                  hintText='Please provide the expiration month and year (MM/YY) associated with this card number.'
                  disabled={this.isSwipe()}
                />
                <Select
                  className='uid-card-exp-year small-input'
                  label='Exp.&nbsp;Year'
                  options={expirationYearOpts()}
                  onChange={valueToState(this.setState.bind(this), 'exp_year')}
                  value={this.state.exp_year}
                  required
                  hintText='Please provide the expiration month and year (MM/YY) associated with this card number.'
                  disabled={this.isSwipe()}
                />
            </If>

            <If is={!displayDropdown}>
                <CustomInput hintText='Please provide the expiration month and year (MM/YY) associated with this card number.'>
                  <PatternGuardedInputText
                    className='uid-card-exp-month small-input'
                    pattern='^[0-9]{0,2}$'
                    label='Expiration Date'
                    onChange={valueToState(this.setState.bind(this), 'exp_month')}
                    value={this.state.exp_month}
                    removeCustomInputWrap={true}
                    placeHolder='MM'
                    disabled={this.isSwipe()}
                  />
                  <span className="input-divider"> / </span>
                  <PatternGuardedInputText
                    className='uid-card-exp-year small-input'
                    pattern='^[0-9]{0,2}$'
                    onChange={valueToState(this.setState.bind(this), 'exp_year')}
                    value={this.state.exp_year}
                    required
                    removeCustomInputWrap={true}
                    placeHolder='YY'
                    disabled={this.isSwipe()}
                  />
                </CustomInput>
            </If>
            <ErrorFeedback
              className='uid-card-exp-month-error-feedback'
              testFn={this.errorFeedbackTestFn('exp_month')}
              name='card/expiration_month' />
            <ErrorFeedback
              className='uid-card-exp-year-error-feedback'
              testFn={this.errorFeedbackTestFn('exp_year')}
              name='card/expiration_year' />
            <If is={!!viewElement.security_code}>
              <PatternGuardedInputText
                className='uid-card-csc small-input'
                pattern='^[0-9]{0,4}$'
                label='CSC'
                autocomplete='off'
                onChange={valueToState(this.setState.bind(this), 'csc')}
                value={this.state.csc}
                required={reqElement.csc}
                placeholder={reqElement.csc ? '' : 'Optional'}
                hintText='Please provide the 3 or 4 digit security code for the card being processed. The CSC can be found usually on the back of the credit card. CSC is an excellent fraud prevention feature.'
              />
              <ErrorFeedback
                className='uid-card-csc-error-feedback'
                testFn={this.errorFeedbackTestFn('csc')}
                name='csc' />
            </If>
            <ErrorFeedback name='card' />

            <SectionTitle title='Contact Details' />
            <If is={!!viewElement.email_address}>
              <InputText
                className='uid-contact-email'
                label='Email'
                maxLength={50}
                onChange={valueToState(this.setState.bind(this), 'billing_email')}
                value={this.state.billing_email}
                placeholder='Optional'
                hintText="Please provide your customer's email address, and you will have the option to email them a receipt."
              />
            </If>
            <If is={!!viewElement.phone_number}>
              <PatternGuardedInputText
                className='uid-contact-phone'
                label='Phone Number'
                pattern='^([0-9\.\(\) -]{0,20})$'
                onChange={valueToState(this.setState.bind(this), 'billing_phone')}
                value={this.state.billing_phone}
                placeholder='Optional'
                hintText="Please provide your customer's phone number without spaces."
              />
            </If>
            {this.maybeRenderDiscData()}

            <If is={!!viewElement.customer_reference_id}>
              <InputText
                className='uid-customer-reference-id'
                label='Customer Reference ID'
                maxLength={50}
                onChange={valueToState(this.setState.bind(this), 'customer_reference_id')}
                value={this.state.customer_reference_id}
                placeholder={this.isSelectedCof() ? '' : 'Optional'}
                hintText="Please provide an identifier for this customer of up to 50 characters.  The default customer ID is the PayTrace transaction ID."
              />
            </If>

            {/* TODO: Swap to a textarea (need core widget?) */}
            <If is={!!viewElement.description}>
              <InputText
                className='uid-description'
                label='Description'
                onChange={valueToState(this.setState.bind(this), 'description')}
                value={this.state.description}
                placeholder='Optional'
                hintText="Please provide any additional information about this transaction such as the included products or other information about the customer."
              />
            </If>

            <SectionTitle title='Billing Address'/>
            <If is={!!viewElement.billing_name}>
              <InputText
                maxLength={50}
                className='uid-billing-name'
                label='Name'
                onChange={valueToState(this.setState.bind(this), 'billing_name')}
                value={this.state.billing_name}
                placeholder='Optional'
                hintText="Please provide the customer's name, preferrably as it is shown on the card."
              />
            </If>
            <If is={!!viewElement.billing_address}>
              <PatternGuardedInputText
                className='uid-billing-address'
                label='Address Line 1'
                pattern="^([a-zA-Z0-9\/\.\-_,#'&+\s\xc0-\xff]){0,75}$"
                onChange={valueToState(this.setState.bind(this), 'billing_address')}
                value={this.state.billing_address}
                required={reqElement.business_address}
                placeholder={reqElement.business_address ? '' : 'Optional'}
                hintText="Please provide your customer's billing address where their bank statement is delivered. We strongly recommend providing a billing address as this information will benefit your transaction qualification.  Additionally, the billing address is validated via AVS (Address Verification System) to help prevent fraud."
              />
            </If>
            <If is={!!viewElement.billing_address2}>
              <PatternGuardedInputText
                className='uid-billing-address2'
                label='Address Line 2'
                pattern="^([a-zA-Z0-9\/\.\-_,#'&+\s\xc0-\xff]){0,50}$"
                onChange={valueToState(this.setState.bind(this), 'billing_address2')}
                value={this.state.billing_address2}
                placeholder='Optional'
                hintText="Please provide any additional elements of your customer's billing address."
              />
            </If>
            <br />
            <If is={!!viewElement.billing_city}>
              <PatternGuardedInputText
                className='uid-billing-city'
                label='City'
                pattern="^[A-Za-z \.'-]{0,50}$"
                onChange={valueToState(this.setState.bind(this), 'billing_city')}
                value={this.state.billing_city}
                placeholder='Optional'
                hintText="Please provide the city where your customer's billing address is located."
              />
            </If>
            <If is={!!viewElement.billing_state}>
              <If is={!!displayDropdown}>
                <StateSelect
                  className='uid-billing-state'
                  label='State'
                  name='billing_state'
                  onChange={valueToState(this.setState.bind(this), 'billing_state')}
                  value={this.state.billing_state}
                  required={this.state.isCardSurchargeable && this.state.canMerchantSurcharge}
                  placeholder={this.state.isCardSurchargeable ? '' : 'Optional'}
                  hintText="Please select the state where your customer's Billing address is located."
                />
              </If>
              <If is={!displayDropdown}>
                <PatternGuardedInputText
                  className='uid-billing-state small-input'
                  label='State'
                  pattern='^[A-Za-z]{0,2}$'
                  onChange={valueToState(this.setState.bind(this), 'billing_state')}
                  value={this.state.billing_state}
                  required={this.state.isCardSurchargeable && this.state.canMerchantSurcharge}
                  placeholder={this.state.isCardSurchargeable ? '' : 'Optional'}
                  hintText="Please select the state where your customer's billing address is located."
                />
              </If>
            </If>
            <If is={!!viewElement.billing_zip}>
              <PatternGuardedInputText
                className='uid-billing-zip small-input'
                label='Zip Code'
                pattern='^[ A-Za-z0-9-]{0,10}$'
                onChange={valueToState(this.setState.bind(this), 'billing_zip_code')}
                value={this.state.billing_zip_code}
                required={reqElement.zip_code}
                placeholder={reqElement.zip_code ? '' : 'Optional'}
                hintText="Please provide your customer's billing ZIP code where their bank statement is delivered. We strongly recommend providing a billing ZIP code as this information will benefit your transaction qualification.  Additionally, the billing ZIP code is validated via AVS (Address Verification System) to help prevent fraud."
              />
            </If>
            <If is={!!viewElement.billing_country}>
              <CountrySelect
                className='uid-billing-country'
                label='Country'
                name='billing_country'
                onChange={valueToState(this.setState.bind(this), 'billing_country')}
                value={this.state.billing_country}
                placeholder='Optional'
                hintText="Please select the country where your customer's billing address is located."
              />
            </If>

          <If is={!!displayShippingElements}>
            <div>
              <SectionTitle title='Shipping Address' />
              <div style={{ marginTop: '1rem', marginLeft: '205px' }}>
                <label className="uid-copy-billing-to-shipping">
                  <input
                    type="checkbox"
                    onChange={this.handleBillingToShipping}
                    checked={this.state.is_billing_to_shipping_checked}
                  />
                  Shipping Address is the same as Billing Address
                </label>
              </div>
            </div>
            <ErrorFeedback
              className='uid-amount-error-feedback'
              name='shipping_address' />
          </If>

            <If is={!!viewElement.shipping_name}>
              <InputText
                className='uid-shipping-name'
                label='Name'
                maxLength={50}
                onChange={valueToState(this.setState.bind(this), 'shipping_name')}
                value={this.state.shipping_name}
                placeholder='Optional'
                hintText="Please provide the customer's Shipping name."
              />
            </If>
            <If is={!!viewElement.shipping_address}>
              <PatternGuardedInputText
                className='uid-shipping-address'
                pattern="^([a-zA-Z0-9\/\.\-_,#'&+\s\xc0-\xff]){0,75}$"
                label='Address Line 1'
                onChange={valueToState(this.setState.bind(this), 'shipping_address')}
                value={this.state.shipping_address}
                placeholder='Optional'
                hintText="Please provide your customer's Shipping address."
              />
            </If>
            <If is={!!viewElement.shipping_address2}>
              <PatternGuardedInputText
                className='uid-shipping-address2'
                label='Address Line 2'
                pattern="^([a-zA-Z0-9\/\.\-_,#'&+\s\xc0-\xff]){0,50}$"
                onChange={valueToState(this.setState.bind(this), 'shipping_address2')}
                value={this.state.shipping_address2}
                placeholder='Optional'
                hintText="Please provide any additional elements of your customer's Shipping address."
              />
            </If>
            <br />
            <If is={!!viewElement.shipping_city}>
              <PatternGuardedInputText
                className='uid-shipping-city'
                pattern="^[A-Za-z \.'-]{0,50}$"
                label='City'
                onChange={valueToState(this.setState.bind(this), 'shipping_city')}
                value={this.state.shipping_city}
                placeholder='Optional'
                hintText="Please provide the city where your customer's Shipping address is located."
              />
            </If>
            <If is={!!viewElement.shipping_state}>
              <If is={!!displayDropdown}>
                <StateSelect
                  className='uid-shipping-state'
                  label='State'
                  name='shipping_state'
                  onChange={valueToState(this.setState.bind(this), 'shipping_state')}
                  value={this.state.shipping_state}
                  placeholder='Optional'
                  hintText="Please select the state where your customer's Shipping address is located."
                />
              </If>
              <If is={!displayDropdown}>
                <PatternGuardedInputText
                  className='uid-shipping-state small-input'
                  pattern='^[A-Za-z]{0,2}$'
                  label='State'
                  maxLength={2}
                  onChange={valueToState(this.setState.bind(this), 'shipping_state')}
                  value={this.state.shipping_state}
                  placeholder='Optional'
                  hintText="Please select the state where your customer's Shipping address is located."
                />
              </If>
              <ErrorFeedback
                className='uid-amount-error-feedback'
                testFn={this.errorFeedbackTestFn('shipping_state')}
                name='shipping_address/state' />
            </If>

            <If is={!!viewElement.shipping_zip}>
              <PatternGuardedInputText
                className='uid-shipping-zip small-input'
                pattern='^[ A-Za-z0-9-]{0,10}$'
                label='Zip Code'
                onChange={valueToState(this.setState.bind(this), 'shipping_zip_code')}
                value={this.state.shipping_zip_code}
                placeholder='Optional'
                hintText="Please provide your customer's Shipping ZIP code."
              />
            </If>
            <If is={!!viewElement.shipping_country}>
              <CountrySelect
                className='uid-shipping-country'
                label='Country'
                name='shipping_country'
                onChange={valueToState(this.setState.bind(this), 'shipping_country')}
                value={this.state.shipping_country}
                placeholder='Optional'
                hintText="Please select the country where your customer's Shipping address is located."
              />
            </If>
            <If is={((this.state.canMerchantSurcharge || this.hasIncompleteSurchargeReasons()) && !isNonSurchargeableAuthType(this.state.auth_type))}>
              <SectionTitle title='Surcharge Details'/>
              <div style={{ display: 'flex' }}>
                <If is={!this.state.omit_surcharge}>
                  <div>
                    <InputText
                      className='uid-surcharge-amount readonly-input'
                      label='Surcharge Amount'
                      name='surcharge_amount'
                      value={this.state.surchargeAmount.toFixed(2)}
                      readOnly
                    />
                    <InputText
                      className='uid-total-amount readonly-input'
                      label='Total Amount'
                      name='total_amount'
                      value={this.calculateTotalWithSurchargeAmount().toFixed(2)}
                      readOnly
                    />
                  </div>
                </If>
                {this.maybeRenderSurchargeFeedback()}
              </div>
              <If is={this.state.canMerchantSurcharge && this.state.surchargeAmount > 0.0}>
                <CustomInput
                  name='surcharge_choice'
                  className='uid-surcharge-choice'
                >
                  <div id='surcharge-choice-block' >
                    <label></label>
                    <div>
                      <div className='surcharge-choice-item'>
                        <input
                          type='radio'
                          id='surcharge_choice-confirm_surcharge'
                          name='surcharge_choice'
                          required={true}
                          checked={this.state.omit_surcharge === false}
                          value='confirm_surcharge'
                          onChange={() => this.setState({ omit_surcharge: false })}
                          />
                          <label htmlFor={'surcharge_choice-confirm_surcharge'}>
                          {'The cardholder has been informed of alternative payment options to avoid ' +
                            'the surcharge and has elected to incur a surcharge fee for this transaction.'}
                        </label>
                      </div>
                      <div className='surcharge-choice-item'>
                        <input
                          type='radio'
                          id='surcharge_choice-omit_surcharge'
                          name='surcharge_choice'
                          checked={this.state.omit_surcharge === true}
                          value='omit_surcharge'
                          onChange={() => this.setState({ omit_surcharge: true })}
                        />
                        <label htmlFor={'surcharge_choice-omit_surcharge'}>
                          {'Process this transaction without a surcharge.'}
                        </label>
                      </div>
                    </div>
                  </div>
                </CustomInput>
              </If>
          </If>

          <ErrorFeedback className='uid-misc-error-feedback' name='root' />
          <ProcessButton
            records={this.context.records}
            required_permissions={[this.getRequiredPermissionToProcess()]}
          />

          </Fieldset>
        </form>
      </>
    )
  }
}

export default VirtualTerminal
